/*
 * Search results in-place navigation (owner gate G12).
 *
 * The app proxy renders the whole results section server-side into
 * #shopify-section-proxy-search. Every facet, chip, sort option and pagination
 * control inside it is a plain <a href="/a/search?...">, so each click paid for
 * a full document load. This intercepts those clicks, refetches the same URL
 * as a fragment, and swaps only the four server-rendered blocks listed in
 * BLOCKS below.
 *
 * The refetch carries FRAGMENT_PARAM, which asks the engine for `{% layout
 * none %}` plus the results section alone -- no theme shell, no stylesheet
 * tags, no inline scripts. Measured 2026-09-04: the old full-page refetch cost
 * about 760 KB per swap and the shell alone cost about 0.2 s of Liquid. The
 * response still contains #shopify-section-proxy-search, so an engine that
 * ignores the flag and returns the whole document still parses and swaps.
 *
 * The <section> root itself is never replaced. assets/search-result-filters.js
 * delegates its listeners from that root (it carries data-sfs-query-filters)
 * and assets/proxy-search-drawer-a11y.js resolves drawer nodes per event, so
 * both keep working after a swap.
 *
 * Anything unexpected -- a bad status, a slow response, markup without the
 * section -- falls back to a normal navigation. A shopper is never stranded.
 */
(function () {
  'use strict';

  var SECTION_ID = 'shopify-section-proxy-search';
  var PROXY_PATH = '/a/search';
  var PROXY_PREFIX = PROXY_PATH + '?';
  var FRAGMENT_PARAM = 'sfs_fragment=1';
  var TIMEOUT_MS = 2500;
  var LOADING_CLASS = 'sfs-results-loading';
  var STATUS_ATTR = 'data-sfs-results-status';
  var CARD_SELECTOR = '.product-card, .card-wide-link, .sfs-tile';
  var BLOCKS = [
    '#FilterDrawerproxy-search',   // filter rail / drawer contents
    '.collection-results-header',  // heading, result count, sort dropdown
    '.collection-results-body',    // pinned card, like rails, product grid
    '.collection-results-footer'   // pagination
  ];

  var controller = null;
  var timer = 0;
  var activeToken = 0;

  function restyleActiveChips(el) {
    if (!el || !el.classList) return;
    el.classList.add('collection-active-filters');
    var clear = el.querySelector('.jsFilterReset, .btn-filter-reset');
    if (clear && clear.classList) clear.classList.add('collection-active-filters__clear');
    var list = el.querySelector('.filter-active-list ul, ul[role="list"]');
    if (list && list.classList) list.classList.add('collection-active-filters__list');
    el.querySelectorAll('li').forEach(function (item) {
      if (item.classList) item.classList.add('collection-active-filters__item');
      var chip = item.querySelector('a, button');
      if (!chip || !chip.classList) return;
      chip.classList.add('collection-active-filters__chip');
      var text = chip.querySelector('.jsDisplayLabel');
      if (text && text.classList) text.classList.add('collection-active-filters__chip-text');
      var icon = chip.querySelector('.icon');
      if (icon && icon.classList) icon.classList.add('collection-active-filters__chip-icon');
    });
  }

  // Current proxy HTML places chips in the results column. Keep the sidebar
  // fallback while older engine releases can still serve a fragment.
  function placeActiveChips(root) {
    if (!root || !root.querySelector) return;
    var sidebar = root.querySelector('.sidebar-section') || root.querySelector('#FilterDrawerproxy-search');
    var sidebarChips = sidebar && sidebar.querySelector('.filter-active, .collection-active-filters');
    var resultsWrap = root.querySelector('.collection-results-wrapper, .search-results-wrapper');
    var resultsChips = resultsWrap && resultsWrap.querySelector(':scope > .filter-active, :scope > .collection-active-filters');
    if (resultsChips) {
      restyleActiveChips(resultsChips);
      if (sidebarChips && sidebarChips !== resultsChips && sidebarChips.remove) sidebarChips.remove();
      return;
    }
    var guide = root.querySelector('[data-facet-guide]');
    var body = root.querySelector('.collection-results-body, .search-results-body');
    var anchor = guide || body;
    var parent = anchor && (anchor.parentNode || anchor.parentElement);
    if (!anchor || !parent || !parent.insertBefore) return;
    if (sidebarChips) {
      if (resultsChips && resultsChips !== sidebarChips && resultsChips.remove) resultsChips.remove();
      restyleActiveChips(sidebarChips);
      if (sidebarChips !== anchor.previousElementSibling) {
        parent.insertBefore(sidebarChips, anchor);
      }
    }
  }

  function onProxyPath() {
    var path = window.location.pathname;
    return path === PROXY_PATH || path === PROXY_PATH + '/';
  }

  function sectionRoot() {
    return document.getElementById(SECTION_ID);
  }

  // Only the request URL carries the flag. The shopper-facing href -- the one
  // pushed to history, and the one a fallback navigation uses -- stays clean.
  // A fragment response is a document, so any hash is dropped before the
  // param is appended.
  function fragmentUrl(href) {
    var hash = href.indexOf('#');
    var base = hash === -1 ? href : href.slice(0, hash);
    return base + (base.indexOf('?') === -1 ? '?' : '&') + FRAGMENT_PARAM;
  }

  function isModifiedClick(event) {
    if (!event) return false;
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return true;
    return typeof event.button === 'number' && event.button > 0;
  }

  function resolveLink(target, root) {
    if (!root || !target || typeof target.closest !== 'function') return null;
    var link = target.closest('a[href]');
    if (!link || !root.contains(link)) return null;
    if (link.getAttribute('target') || link.hasAttribute('download')) return null;
    if (link.closest(CARD_SELECTOR)) return null;
    var href = link.getAttribute('href') || '';
    return href.indexOf(PROXY_PREFIX) === 0 ? link : null;
  }

  // Pair every block first, then apply, so the DOM is never half swapped. A
  // block the shopper can see with no replacement in the response (results page
  // to a zero-results page) would strand stale cards, so that reports failure
  // and the caller navigates instead.
  function swapBlocks(root, incoming) {
    var pairs = [];
    for (var i = 0; i < BLOCKS.length; i += 1) {
      var current = root.querySelector(BLOCKS[i]);
      var next = incoming.querySelector(BLOCKS[i]);
      if (current && !next) return 0;
      if (current && next) pairs.push([current, next]);
    }
    var currentWrap = root.querySelector('.collection-results-wrapper, .search-results-wrapper');
    var nextWrap = incoming.querySelector('.collection-results-wrapper, .search-results-wrapper');
    var nextChips = nextWrap && nextWrap.querySelector(':scope > .filter-active, :scope > .collection-active-filters');
    var anchor = currentWrap && (currentWrap.querySelector('[data-facet-guide]') || currentWrap.querySelector('.collection-results-body, .search-results-body'));
    if (nextChips && (!currentWrap || !anchor)) return 0;
    preserveDisclosures(root, incoming);
    for (var j = 0; j < pairs.length; j += 1) {
      pairs[j][0].replaceWith(document.importNode(pairs[j][1], true));
    }
    // Chips are optional, unlike the four required blocks. A response with
    // no chips must clear an old strip. Keep the guide itself in place.
    if (currentWrap) {
      currentWrap.querySelectorAll(':scope > .filter-active, :scope > .collection-active-filters').forEach(function (strip) { strip.remove(); });
      if (nextChips) {
        anchor = currentWrap.querySelector('[data-facet-guide]') || currentWrap.querySelector('.collection-results-body, .search-results-body');
        currentWrap.insertBefore(document.importNode(nextChips, true), anchor);
      }
    }
    return pairs.length;
  }

  // Category-style groups are permanently hidden via CSS and never count
  // toward the "first three" default-open budget; they close on every swap
  // regardless of any state a stale swap may have recorded for them.
  var INELIGIBLE_DISCLOSURE_GROUPS = {
    category: true,
    'product-category': true,
    'breaker-category': true
  };

  function isEligibleDisclosureGroup(filterBlock) {
    if (!filterBlock || !filterBlock.classList) return true;
    for (var i = 0; i < filterBlock.classList.length; i += 1) {
      if (INELIGIBLE_DISCLOSURE_GROUPS[filterBlock.classList[i]]) return false;
    }
    return true;
  }

  function preserveDisclosures(root, incoming) {
    if (!root.querySelectorAll || !incoming.querySelectorAll) return;
    var selector = '.filter-block > details';
    var states = new Map();
    function key(detail) {
      var label = detail.querySelector('summary .label') || detail.querySelector('summary');
      return label ? label.textContent.replace(/\s+/g, ' ').trim() : '';
    }
    root.querySelectorAll(selector).forEach(function (detail) {
      var name = key(detail);
      if (name) states.set(name, detail.open);
    });
    var eligiblePosition = 0;
    incoming.querySelectorAll(selector).forEach(function (detail) {
      var name = key(detail);
      var eligible = isEligibleDisclosureGroup(detail.parentElement);
      if (!eligible) {
        detail.open = false;
      } else {
        detail.open = states.has(name) ? states.get(name) : eligiblePosition < 3;
        eligiblePosition += 1;
      }
      detail.setAttribute('data-sfs-disclosure-ready', '');
    });
  }

  function announce(root, message) {
    if (!root) return;
    var status = root.querySelector('[' + STATUS_ATTR + ']');
    if (!status) {
      status = document.createElement('p');
      status.setAttribute(STATUS_ATTR, '');
      status.className = 'sfs-results-status';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      root.appendChild(status);
    }
    status.textContent = message;
  }

  function revealHeader(root) {
    if (!root) return;
    var header = root.querySelector('.collection-results-header');
    if (!header || typeof header.getBoundingClientRect !== 'function') return;
    if (header.getBoundingClientRect().top >= 0) return;
    if (typeof header.scrollIntoView === 'function') header.scrollIntoView();
  }

  function setBusy(root, busy) {
    if (!root || !root.classList) return;
    if (busy) root.classList.add(LOADING_CLASS);
    else root.classList.remove(LOADING_CLASS);
    root.setAttribute('aria-busy', busy ? 'true' : 'false');
    root.querySelectorAll('a[href]').forEach(function (link) {
      if (!resolveLink(link, root)) return;
      if (busy && !link.hasAttribute('aria-disabled')) {
        link.setAttribute('data-sfs-busy-disabled', '');
        link.setAttribute('aria-disabled', 'true');
      } else if (!busy && link.hasAttribute('data-sfs-busy-disabled')) {
        link.removeAttribute('data-sfs-busy-disabled');
        link.removeAttribute('aria-disabled');
      }
    });
  }

  function load(href, pushHistory, pagination, analyticsIntent) {
    if (window.SFSNativeFallback && window.SFSNativeFallback.enabled()) {
      return window.SFSNativeFallback.handleResultsLink(href, pushHistory ? 'push' : 'replace', analyticsIntent);
    }
    var root = sectionRoot();
    if (!root || typeof window.fetch !== 'function') {
      window.location.assign(href);
      return;
    }

    activeToken += 1;
    var token = activeToken;
    if (controller) {
      try { controller.abort(); } catch (error) { /* already settled */ }
    }
    if (timer) window.clearTimeout(timer);

    var request = null;
    try { request = new AbortController(); } catch (error) { request = null; }
    controller = request;

    timer = window.setTimeout(function () {
      if (request) request.abort();
    }, TIMEOUT_MS);

    var options = { credentials: 'same-origin', headers: { Accept: 'text/html' } };
    if (request) options.signal = request.signal;
    setBusy(root, true);

    function settle() {
      if (token !== activeToken) return;
      window.clearTimeout(timer);
      timer = 0;
      controller = null;
      setBusy(sectionRoot(), false);
    }

    window.fetch(fragmentUrl(href), options).then(function (response) {
      if (!response || !response.ok) throw new Error('search results request failed');
      return response.text();
    }).then(function (html) {
      if (token !== activeToken) return;
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var incoming = parsed.getElementById(SECTION_ID);
      var live = sectionRoot();
      if (!incoming || !live) throw new Error('search results section missing');
      if (!swapBlocks(live, incoming)) throw new Error('search results blocks missing');
      placeActiveChips(live);
      if (pushHistory) window.history.pushState({ sfs: 1 }, '', href);
      if (parsed.title) document.title = parsed.title;
      announce(live, 'Results updated');
      window.dispatchEvent(new Event('sfs:results-updated'));
      if (window.SBAnalytics && analyticsIntent) window.SBAnalytics.filterCommit(analyticsIntent, live);
      if (pagination) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      else revealHeader(live);
      settle();
    })['catch'](function () {
      if (token !== activeToken) return;
      settle();
      window.location.assign(href);
    });
  }

  function onClick(event) {
    if (event.defaultPrevented || isModifiedClick(event)) return;
    var root = sectionRoot();
    var link = resolveLink(event.target, root);
    if (!link) return;
    event.preventDefault();
    if (root.classList.contains(LOADING_CLASS)) {
      announce(root, 'Updating results. Please wait before selecting another filter.');
      return;
    }
    var isPagination = !!link.closest('.collection-results-footer');
    var analyticsIntent = !isPagination && !link.closest('.sort-section') && window.SBAnalytics ? window.SBAnalytics.filterIntent(link) : null;
    load(link.getAttribute('href'), true, isPagination, analyticsIntent);
  }

  function onPopState() {
    if (!onProxyPath() || !sectionRoot()) return;
    load(window.location.href, false);
  }

  if (!onProxyPath() || window.__sbSearchResultsNavBound) return;
  window.__sbSearchResultsNavBound = true;

  // The proxy can replace the section node tree, so resolve nodes per event
  // from the document rather than holding a reference to the section root.
  document.addEventListener('click', onClick);
  window.addEventListener('popstate', onPopState);
  placeActiveChips(sectionRoot());

  // Exposed for the contract test only.
  window.SFS_RESULTS_NAV = {
    BLOCKS: BLOCKS,
    TIMEOUT_MS: TIMEOUT_MS,
    isModifiedClick: isModifiedClick,
    resolveLink: resolveLink,
    swapBlocks: swapBlocks,
    preserveDisclosures: preserveDisclosures,
    placeActiveChips: placeActiveChips,
    load: load,
    onClick: onClick
  };
})();
